import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TextField } from "@mui/material";
import AutocompleteElement from "@atoms/autocomplete";
import Datepicker from "@atoms/datepicker";
import AutocompleteDynamic from "@atoms/autocompleteDynamic";
import RangeSliderWithInputs from "@atoms/range";
import { ConfigType, FiltersState } from "../../helpers/map-filter-element";
import { debounce } from "@helpers/debounce";
import { FiltersType } from "../../types";
import { getMapper } from "@helpers/get-option-mapper";
import getExtraParams from "@helpers/get-filter-params";
import dayjs from "@helpers/dayjs";

type Props = {
  filterIds: string[];
  filters: FiltersState;
  setFilters: any;
  fieldsMapper: Record<string, ConfigType>;
  params?: Record<string, any>;
  filterObject?: Record<string, any>;
  setFilterObject?: (
    update:
      | Record<string, any>
      | ((prev: Record<string, any>) => Record<string, any>),
  ) => void;
};

const DynamicFilters: React.FC<Props> = ({
  filterIds,
  filters,
  setFilters,
  fieldsMapper,
  params,
  filterObject,
  setFilterObject,
}) => {
  const keysRef = useRef<Record<string, string>>({});
  const [localInput, setLocalInput] = useState<{ [key: string]: string }>({});

  const dynamicIds = useMemo(
    () =>
      filterIds.filter(
        (id) =>
          fieldsMapper[id]?.element === "Autocomplete" &&
          fieldsMapper[id]?.dynamic,
      ),
    [filterIds],
  );

  useEffect(() => {
    filterIds.forEach((id) => {
      const filterItem = fieldsMapper[id];
      if (filterItem?.element === "Textfield") {
        if (filters[id] != null && filters[id] !== "") {
          setLocalInput((prev) => ({ ...prev, [id]: String(filters[id]) }));
        } else if (filters[id] == null) {
          setLocalInput((prev) => ({ ...prev, [id]: "" }));
        }
      }
    });
  }, [filters, filterIds, fieldsMapper]);

  useEffect(() => {
    if (Object.keys(filters).length === 0) {
      setLocalInput({});
    }
  }, [filters]);

  const isNullStates = dynamicIds.map((id) => filters[id] === null);

  const dynamicProps = useMemo(() => {
    return dynamicIds.reduce(
      (acc, id) => {
        if (filters[id] === null) {
          keysRef.current[id] = `${id}-${Math.random()}`;
        } else if (!keysRef.current[id]) {
          keysRef.current[id] = id;
        }

        acc[id] = {
          key: keysRef.current[id],
          initial: filters[id],
        };

        return acc;
      },
      {} as Record<string, { key: string; initial: any }>,
    );
  }, [filters, dynamicIds, isNullStates]);

  useEffect(() => {
    if (Object.keys(filters).length === 0) {
      setLocalInput({});
    }
  }, [filters]);

  const debouncedSearch = useMemo(
    () =>
      debounce((params: { value: string; field: string }) => {
        const { value, field } = params;
        setFilters((prev: FiltersType) => ({
          ...prev,
          [field]: value,
        }));
      }, 300),
    [],
  );

  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => {
    const value = e.target.value;
    setLocalInput((prev) => ({ ...prev, [field]: value }));
    debouncedSearch({ value, field });
  };

  const Dynamic = useCallback(
    (filterId: string | number, config: ConfigType) => {
      return (
        <AutocompleteDynamic
          key={dynamicProps[filterId].key}
          label={config.label}
          multi={Boolean(config.multi)}
          size="small"
          queryParam={(config.query as string) || "name"}
          valueKey={config.valueKey as string}
          limit={1}
          {...(filterObject && {
            initial: filterObject[filterId] as
              | { id: number; label: string }
              | { id: number; label: string }[]
              | null,
          })}
          updateFields={(value: any) => {
            setFilters((prev: FiltersType) => {
              return {
                ...prev,
                [filterId]: value,
              };
            });
          }}
          onSelectOption={(value: any) => {
            setFilterObject?.((prev: Record<string, any>) => {
              return {
                ...prev,
                [filterId]: value,
              };
            });
          }}
          optionMapper={
            config.mapperKey ? getMapper(config.mapperKey) : undefined
          }
          extraParams={getExtraParams(
            config.extraParams as string[],
            params as Record<string, any>,
          )}
          endpoint={config.api as string}
          none={config.none}
          sx={{ maxWidth: "100%", flex: 1 }}
          initialLoadOnOpen
        />
      );
    },
    [dynamicProps, setFilters, filterObject, setFilterObject, params],
  );

  return filterIds.map((filterId) => {
    const config = fieldsMapper[filterId];
    if (!config) return null;

    switch (config.element) {
      case "Textfield":
        return (
          <TextField
            key={filterId}
            label={config.label}
            size="small"
            value={localInput[filterId] ?? ""}
            autoComplete="off"
            sx={{ width: "270px" }}
            onChange={(e) => handleTextChange(e, filterId)}
          />
        );

      case "Autocomplete": {
        if (config.dynamic) {
          return Dynamic(filterId, config);
        } else {
          return (
            <AutocompleteElement
              key={filterId}
              label={config.label}
              options={config.options ?? []}
              groupBy={(option) =>
                typeof option === "string" ? "" : (option.section ?? "")
              }
              limit={1}
              size="small"
              restricted={config.restricted}
              multi={Boolean(config.multi)}
              value={
                config.multi
                  ? (config.options ?? []).filter((opt) =>
                      (filters[filterId] as string[] | null)?.includes(opt.id),
                    )
                  : ((config.options ?? []).find(
                      (opt) => opt.id === filters[filterId],
                    ) ?? null)
              }
              sx={{ maxWidth: "100%", flex: 1 }}
              onChange={(_: any, value: any | any[]) => {
                setFilters((prev: FiltersType) => {
                  const updated = { ...prev } as Record<string, any>;

                  if (config.multi) {
                    if (Array.isArray(value) && value.length > 0) {
                      updated[filterId] = value.map((v: any) => v.id);
                    } else {
                      updated[filterId] = null;
                    }
                  } else {
                    if (value && value.id != null) {
                      updated[filterId] = value.id;
                    } else {
                      updated[filterId] = null;
                    }
                  }

                  return updated as FiltersType;
                });
              }}
            />
          );
        }
      }

      case "Range": {
        return (
          <RangeSliderWithInputs
            key={filterId}
            label={config.label}
            value={
              Array.isArray(filters[filterId]) && filters[filterId].length === 2
                ? (filters[filterId] as [number | null, number | null])
                : [null, null]
            }
            onChange={(newValue: [number | null, number | null]) => {
              setFilters((prev: FiltersType) => ({
                ...prev,
                [filterId]: newValue,
              }));
            }}
          />
        );
      }

      case "Datepicker": {
        const filterValue = filters[filterId];
        const toDate = (val: any) => {
          if (!val) return null;
          const date = new Date(val);
          return isNaN(date.getTime()) ? null : date;
        };

        const isRange = config.range === true;

        const start = isRange ? toDate(filterValue?.[0]) : toDate(filterValue);
        const end = isRange ? toDate(filterValue?.[1]) : null;

        return (
          <Datepicker
            isClearable={true}
            key={filterId}
            label={config.label}
            selectsRange={config.range as boolean}
            start={start}
            end={end}
            width="100%"
            onChangeCallback={(start: string | null, end?: string) => {
              let value: any = null;

              if (start) {
                if (config.range) {
                  const startDate = dayjs(start).toISOString();
                  const endDate = end
                    ? dayjs(end).endOf("day").toISOString()
                    : null;

                  value = [startDate, endDate];
                } else {
                  value = dayjs(start).endOf("day").toISOString();
                }
              }
              setFilters((prev: FiltersType) => ({
                ...prev,
                [filterId]: value,
              }));
            }}
          />
        );
      }

      default:
        return null;
    }
  });
};

export default DynamicFilters;
